import React from 'react';
import { motion } from 'framer-motion';

const sections = [
  {
    title: 'Information We Collect',
    content: [
      {
        subtitle: 'Information You Provide',
        text: 'When you fill out our contact form, book a call, or engage with our services, we collect information such as your name, email address, company name, job title, and any other information you choose to provide about your business challenges and goals.',
      },
      {
        subtitle: 'Automatically Collected Information',
        text: 'We automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, and information about how you interact with our website. We use cookies and similar tracking technologies to collect this information.',
      },
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      {
        text: 'We use the information we collect to:',
        list: [
          'Respond to your inquiries and schedule consultations',
          'Provide, maintain, and improve our services',
          'Send you updates about our services (with your consent)',
          'Analyze website usage to improve user experience',
          'Comply with legal obligations',
          'Protect against fraudulent or unauthorized activity',
        ],
      },
    ],
  },
  {
    title: 'Information Sharing',
    content: [
      {
        text: 'We do not sell, trade, or rent your personal information to third parties. We may share your information with:',
        list: [
          'Service providers who assist in our operations (hosting, analytics, email)',
          'Professional advisors (lawyers, accountants) as needed',
          'Law enforcement when required by law',
          'Business partners with your explicit consent',
        ],
      },
    ],
  },
  {
    title: 'Data Security',
    content: [
      {
        text: 'We implement industry-standard security measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.',
      },
    ],
  },
  {
    title: 'Your Rights',
    content: [
      {
        text: 'Depending on your location, you may have the following rights regarding your personal information:',
        list: [
          'Access the personal information we hold about you',
          'Request correction of inaccurate information',
          'Request deletion of your information',
          'Object to or restrict processing of your information',
          'Data portability',
          'Withdraw consent at any time',
        ],
      },
    ],
  },
  {
    title: 'Cookies',
    content: [
      {
        text: 'We use cookies and similar technologies to enhance your experience on our website. You can control cookie settings through your browser preferences. Disabling cookies may limit some functionality of our website.',
      },
    ],
  },
  {
    title: 'Third-Party Links',
    content: [
      {
        text: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.',
      },
    ],
  },
  {
    title: 'Children\'s Privacy',
    content: [
      {
        text: 'Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it.',
      },
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      {
        text: 'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.',
      },
    ],
  },
  {
    title: 'Contact Us',
    content: [
      {
        text: 'If you have any questions about this Privacy Policy or our data practices, please contact us at:',
      },
    ],
  },
];

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <main className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        {/* Animated background blobs */}
        <motion.div
          className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <div className="relative w-full max-w-4xl mx-auto px-6 lg:px-8 py-32">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">Legal</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]"
          >
            Privacy Policy
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-xl text-zinc-400 leading-relaxed mb-4"
          >
            Your privacy matters to us. This policy explains how we collect, use, and protect your information.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="text-zinc-500 text-sm"
          >
            Last Updated: February 2026
          </motion.p>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Content Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.05, ease: "easeOut" }}
                className="relative"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center mt-1">
                    <span className="text-zinc-400 text-sm font-semibold">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
                      {section.title}
                    </h2>
                    <div className="space-y-4">
                      {section.content.map((item, itemIndex) => (
                        <div key={itemIndex}>
                          {'subtitle' in item && item.subtitle && (
                            <h3 className="text-lg font-semibold text-zinc-300 mb-2">
                              {item.subtitle}
                            </h3>
                          )}
                          {item.text && (
                            <p className="text-zinc-400 leading-relaxed">
                              {item.text}
                            </p>
                          )}
                          {'list' in item && item.list && (
                            <ul className="mt-3 space-y-2">
                              {item.list.map((listItem: string, listIndex: number) => (
                                <motion.li
                                  key={listIndex}
                                  initial={{ opacity: 0, x: -10 }}
                                  whileInView={{ opacity: 1, x: 0 }}
                                  viewport={{ once: true }}
                                  transition={{ delay: listIndex * 0.05 }}
                                  className="flex items-start gap-3 text-zinc-400"
                                >
                                  <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>{listItem}</span>
                                </motion.li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {index < sections.length - 1 && (
                  <div className="mt-12 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
                )}
              </motion.div>
            ))}

            {/* Contact Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative mt-8"
            >
              <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-500/30 via-violet-500/20 to-blue-500/30 rounded-2xl" />
              <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-2xl p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Questions about your data?
                    </h3>
                    <p className="text-zinc-400">
                      Email us at{' '}
                      <a href="mailto:privacy@aideploy.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                        privacy@aideploy.com
                      </a>
                    </p>
                  </div>
                  <motion.a
                    href="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Contact Us
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="relative py-16">
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            Back to Home
          </motion.a>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
